import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import type { PostureFinding } from "./use-posture.ts";
import {
  postureKindLabel,
  findingCodeLabel,
  riskTierLabel,
  riskTierStatus,
  riskTierTitle,
  riskTierRank,
  riskTierProminent,
  findingHref,
} from "./vocab.ts";

/**
 * The individual NHI/host findings list (task 11.10; design 12.2 Posture row; 12.3 drill-down; 12.4
 * semantic table + risk tier by icon+text). It complements the by-code-and-tier grid: that grid is
 * the aggregate count view, this is the row-level view of each finding so an operator can see the
 * agent, source and age behind the numbers.
 *
 * Rows are sorted by risk tier (most severe first) then by most-recently-seen, so critical/high
 * findings surface at the top (design 12.2). Each row's RISK TIER is a `StatusBadge` (icon + text,
 * never colour alone — design 12.4), and the most serious rows carry a non-colour `data-prominent`
 * emphasis. Every row drills to the events behind the finding (`category=posture` + code + tier +
 * source, plus agent when present) via the shared `DenseTable` with a trailing action.
 *
 * The agent is nullable per contract (a host finding has no agent); a null is shown honestly as
 * "No agent" rather than a fabricated id.
 */

export interface FindingsListProps {
  readonly findings: readonly PostureFinding[];
}

export function FindingsList(props: FindingsListProps): ReactNode {
  const navigate = useNavigate();
  const rows = [...props.findings].sort(
    (a, b) =>
      riskTierRank(b.risk_tier) - riskTierRank(a.risk_tier) || b.last_seen - a.last_seen,
  );

  const columns: readonly Column<PostureFinding>[] = [
    {
      key: "tier",
      header: "Risk tier",
      isRowHeader: true,
      cell: (f) => (
        <span
          className="pst-tier-cell"
          data-prominent={riskTierProminent(f.risk_tier) ? "true" : undefined}
        >
          <StatusBadge
            level={riskTierStatus(f.risk_tier)}
            label={riskTierLabel(f.risk_tier)}
            title={riskTierTitle(f.risk_tier)}
            size="sm"
          />
        </span>
      ),
    },
    {
      key: "code",
      header: "Finding",
      cell: (f) => findingCodeLabel(f.code),
    },
    {
      key: "kind",
      header: "Kind",
      cell: (f) => <span className="pst-faint">{postureKindLabel(f.kind)}</span>,
    },
    {
      key: "agent",
      header: "Agent",
      cell: (f) =>
        f.agent === null ? (
          <span className="pst-faint">No agent</span>
        ) : (
          <span className="pst-mono">{f.agent}</span>
        ),
    },
    {
      key: "last_seen",
      header: "Last seen",
      align: "right",
      cell: (f) => (
        <time className="pst-num" dateTime={isoAttr(f.last_seen)}>
          {formatDateTime(f.last_seen)}
        </time>
      ),
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      cell: (f) => (
        <button
          type="button"
          className="pst-link"
          onClick={() => navigate(findingHref(f))}
          aria-label={`Open the events for the ${riskTierLabel(
            f.risk_tier,
          ).toLowerCase()}-tier ${findingCodeLabel(f.code).toLowerCase()} finding`}
        >
          Events
        </button>
      ),
    },
  ];

  return (
    <section className="pst-section" aria-label="Findings">
      <div className="pst-section__head">
        <h2 className="pst-section__title">Findings</h2>
        <p className="pst-section__note">
          Each NHI or host finding, most severe first. A row drills to its events.
        </p>
      </div>
      <DenseTable
        caption="Individual NHI and host findings with risk tier, code, agent and last-seen time"
        captionHidden
        columns={columns}
        rows={rows}
        rowKey={(f) => f.id}
        stickyHeader
        emptyMessage="No individual findings in this window. The by-code-and-tier counts above summarise the window; widen the time range to see more."
      />
    </section>
  );
}
